import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Sparkles, X } from 'lucide-react'
import { KiArbeitet } from '@/components/ki/KiArbeitet'
import { apiErrorText } from '@/lib/api-error'
import { fetchKiLaeufe, fetchKiLauf, kiUebernehmen, kiVerwerfen, type KiLauf, type KiSicherheit, type KiZeile } from '../api/calculation-ai.api'
import { datumText } from '../lib/kalkulation-labels'

const SICHERHEIT: Record<KiSicherheit, { text: string; klasse: string }> = {
  HIGH: { text: 'hoch', klasse: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  MEDIUM: { text: 'mittel', klasse: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  LOW: { text: 'niedrig', klasse: 'bg-amber-500/20 text-amber-800 dark:text-amber-300' },
}

/**
 * AP-37 Detailblatt N 7. (N7) — **Hinweise der KI** zur Prüfung vor der Abgabe: die letzte Prüfung
 * mit Regeln (ohne KI) und Hinweisen der KI, je Hinweis Sicherheit, Sprung zur Position bzw. ins
 * Cockpit, „erledigt" oder „verwerfen". Gestartet wird ausdrücklich („KI-Prüfung starten …") —
 * sie kostet. Die Prüfung warnt, sie sperrt nicht.
 */
export function KiPruefung({
  kalkulationId,
  beschreibbar,
  stand,
  onStarten,
  onPosition,
  onGeaendert,
}: {
  kalkulationId: string
  beschreibbar: boolean
  /** ändert sich, wenn ein Lauf endet — dann neu lesen */
  stand: string
  onStarten: () => void
  onPosition: (id: string) => void
  onGeaendert: () => void
}) {
  const [lauf, setLauf] = useState<(KiLauf & { lines: KiZeile[] }) | null>(null)
  const [laufend, setLaufend] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(() => {
    void fetchKiLaeufe(kalkulationId)
      .then(async (r) => {
        // Fertige Prüfungen beider Ausführer zeigen; „prüft …" nur für den eingebauten Weg (ein Auftrag an die eigene KI wartet Tage).
        const pruefungen = r.data.filter((l) => l.capability === 'CHECK')
        setLaufend(pruefungen.some((l) => l.executor === 'SYMPHENIA' && (l.status === 'QUEUED' || l.status === 'RUNNING')))
        const fertig = pruefungen.find((l) => l.status === 'DONE' || l.status === 'PARTIAL')
        setLauf(fertig ? await fetchKiLauf(kalkulationId, fertig.id) : null)
      })
      .catch((e) => setFehler(apiErrorText(e, 'Die KI-Prüfung ließ sich nicht laden.')))
  }, [kalkulationId])
  useEffect(laden, [laden, stand])

  async function entscheiden(z: KiZeile, erledigt: boolean) {
    if (!lauf) return
    try {
      if (erledigt) await kiUebernehmen(kalkulationId, lauf.id, [z.id], [])
      else await kiVerwerfen(kalkulationId, lauf.id, [z.id])
      laden()
      onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das ließ sich nicht speichern.'))
    }
  }

  const offen = lauf?.lines.filter((z) => z.status === 'OPEN') ?? []
  const erledigt = lauf ? lauf.lines.length - offen.length : 0
  return (
    <section className="rounded-lg border border-primary/30 bg-card p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <Sparkles className="h-4 w-4 text-primary" /> Hinweise der KI
          {lauf?.finishedAt && <span className="font-normal text-muted-foreground">· geprüft {datumText(lauf.finishedAt, true)}</span>}
        </h3>
        {beschreibbar && (
          <button type="button" disabled={laufend} className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50" onClick={onStarten}>
            <Sparkles className="h-3.5 w-3.5" /> {laufend ? <KiArbeitet text="KI prüft" /> : lauf ? 'Erneut prüfen …' : 'KI-Prüfung starten …'}
          </button>
        )}
      </div>
      {!lauf && !laufend && (
        <p className="text-xs text-muted-foreground">
          Vergessene Kosten, Vorbemerkungen, Widersprüche und Mengen gegen die Unterlagen — dazu Regeln ohne KI: Ausreißer, nicht übernommene Angebote, fehlende Preise. Die Prüfung kostet Tokens und läuft
          nur auf Knopfdruck; sie warnt, sie sperrt nichts.
        </p>
      )}
      {lauf && (
        <>
          <p className="mb-2 text-xs text-muted-foreground">
            {offen.length ? `${offen.length} offen` : 'nichts offen'}
            {erledigt ? ` · ${erledigt} erledigt oder verworfen` : ''}
            {lauf.report?.notes?.length ? ` · ${lauf.report.notes.join(' · ')}` : ''}
          </p>
          <ul className="space-y-1.5">
            {offen.map((z) => (
              <li key={z.id} className="flex items-start gap-2 rounded-md border border-border/60 px-2.5 py-1.5 text-sm">
                <div className="min-w-0 flex-1">
                  <div>{String(z.payload.text ?? '')}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
                    <span className={`rounded px-1.5 ${SICHERHEIT[z.confidence].klasse}`}>{SICHERHEIT[z.confidence].text}</span>
                    {z.payload.rule === true && <span className="text-muted-foreground">Regel, ohne KI</span>}
                    {z.targetPositionId && z.position && (
                      <button type="button" className="text-primary hover:underline" onClick={() => onPosition(z.targetPositionId!)}>
                        Pos. {z.position.label} {z.position.shortText}
                      </button>
                    )}
                    {typeof z.payload.link === 'string' && (
                      <Link to={z.payload.link} className="text-primary hover:underline">
                        öffnen
                      </Link>
                    )}
                  </div>
                </div>
                {beschreibbar && (
                  <span className="flex shrink-0 gap-1">
                    <button type="button" className="rounded p-1 text-muted-foreground hover:bg-emerald-500/10 hover:text-emerald-700" title="erledigt" onClick={() => void entscheiden(z, true)}>
                      <Check className="h-4 w-4" />
                    </button>
                    <button type="button" className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" title="verwerfen" onClick={() => void entscheiden(z, false)}>
                      <X className="h-4 w-4" />
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
    </section>
  )
}
